var Component  = require("enyo/Component"),
    Control    = require("enyo/Control"),
    deepEqual  = require("fast-deep-equal"),
    kind       = require("enyo/kind"),
    logger     = require("enyo/logger");
module.exports = kind(/** @lends  app.core.ViewController.prototype */ {

    /**
     * @private
     */
    name: "app.core.ViewController",

    /**
     * @private
     */
    kind: Component,

    /**
     * The instance of the current view.
     *
     * @type     {?enyo.Control}
     * @default  null
     * @readonly
     * @private
     */
    view: null,

    /**
     *
     * The control that will host the view.
     *
     * @type     {?enyo.Control}
     * @default  null
     * @private
     */
    viewContainer: null,

    /**
     * Whether the controller is currently activating a view.
     *  * -1 = inactive
     *  *  0 = activating
     *  *  1 = active
     *
     * @type  {number}
     * @readonly
     * @public
     */
    active: -1,

    /**
     * @type  {Object}
     * @private
     */
    activation: null,

    /**
     * State and data for this view.
     *
     * @type  {?Object}
     * @readonly
     * @public
     */
    state: null,

    /**
     * Activate this popup content.
     *
     * @param    {Object}   params  The parameters
     * @returns  {Promise}  Whether the activation was succesfull.
     * @public
     */
    activate: function(params) {
        var activation  = this.activation,
            fingerprint = this.buildActivationFingerprint(params);

        // Test if we can return a pending activation promise
        if (activation && !activation.finished) {
            // Nope, the activation doesn't match our current needs.
            if (!deepEqual(activation.fingerprint, fingerprint)) {
                activation.resolve(-1);
            }

            // We get to be lazy! Woohoo!
            else {
                return activation.promise;
            }
        }

        // New activation needed
        this.activation    = activation = {
            fingerprint: fingerprint,
            params:      params,
            finished:    false
        };
        activation.promise = new Promise(this.bindSafely(function(resolve, reject) {
            activation.resolve = this.bindSafely(function(active, callback) {
                if (typeof Sentry !== "undefined" && active !== 1 && active !== -1) {
                    Sentry.captureException(new Error("Illegal final view activation value."), {
                        extra: {
                            active: active
                        }
                    });
                }
                this.set("active", active);
                activation.finished = true;
                callback            && callback();
                resolve();
            });

            activation.reject  = this.bindSafely(function(reason) {
                this.set("active", -1);
                activation.finished = true;
                reject(reason);
            });
        }));

        // Execute the activation
        this.resetAppState();
        this.set("active", 0);
        this.execute(activation);

        // Return the activation promise
        return activation.promise;
    },

    /**
     * Set the initial app state before executing the activation. Extend or
     * override as needed.
     *
     * @protected
     * @abstract
     */
    resetAppState: function() {
        var errorMsg = "app.core.ViewController.resetAppState() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }
    },

    /**
     * Build an activation fingerprint. Extend or override as needed.
     *
     * @param    {Object}  params  The parameters
     * @returns  {Object}  The fingerprint
     * @protected
     * @abstract
     */
    buildActivationFingerprint: function() {
        var errorMsg = "app.core.ViewController.buildActivationFingerprint() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }
    },

    /**
     * Implement the logic for loading and displaying the content here.
     *
     * @param  {Object}    activation           The activation object
     * @param  {Object}    activation.params    Parameters to tell us what to do
     * @param  {boolean}   activation.finished  Flag if activation is finished
     * @param  {function}  activation.resolve   A promise resolver to fire when
     *                                          the controller is active.
     * @param  {function}  activation.reject    A promise rejector to fire when
     *                                          the controller failed to
     *                                          activate.
     * @public
     * @abstract
     */
    execute: function() {
        var errorMsg = "app.core.ViewController.execute() not implemented";

        logger.warn(errorMsg);

        if (typeof Sentry !== "undefined") {
            Sentry.captureException(new Error(errorMsg));
        }
    },

    /**
     * Tell the Controller which view to use. Pass a constructor, an instance or
     * `null`. In case of `null` any existing view will be destroyed.
     *
     * @param  {?Function|enyo.Control}  view  A kind constructor, a kind
     *                                         instance or `null`
     * @protected
     */
    updateView: function(view) {
        var currentView = this.view;

        // No view change, bail early
        if (currentView === view) {
            return;
        }

        // No view
        if (!view) {
            currentView && this.destroyView(currentView);

            return;
        }

        // Current view must be replaced with a new instance
        if (this.isKindConstructor(view) && !(currentView instanceof view)) {
            currentView && this.destroyView(currentView);
        }

        // Current view will be replaced by the provided one
        else if (view instanceof Control && view !== currentView) {
            currentView && this.destroyView(currentView);
        }

        // New view must be instantiated first
        if (this.isKindConstructor(view)) {
            view = this.createView(view);
        }

        // Somehow we cannot create the View
        if (!view) {
            throw new Error("Cannot create View");
        }

        // Mount the view.
        this.mountView(view);
    },

    /**
     * There isn't an official way to detect enyo Kind constructors supplied by
     * Enyo itself, but we can look for a highly specialized group of prototype
     * properties.
     *
     * Chances that we receive a function that matches all these criteria but
     * still isn't an enyo Kind are probably zero.
     *
     * @param   {*}        prop  Probably either a view instance or view
     *                           Constructor.
     * @returns {boolean}  Whether the view is a kind constructor
     */
    isKindConstructor: function(prop) {
        return typeof prop === "function"

            // Kind constructors have a prototype
            && prop.prototype

            // Kind constructors self-reference on prototype
            && prop.prototype.ctor === prop

            // Kind constructors cache their super-class constructor on the
            // prototype. Even if there was no super-class, the property is
            // still defined.
            && "base" in prop.prototype

            // Kind constructors reference the kind name on prototype. Even if
            // the constructor is anonymous, the property is still defined.
            && "kindName" in prop.prototype;
    },

    /**
     * Instantiate a new view.
     *
     * @param    {Function}      ViewKind  The view kind constructor.
     * @returns  {enyo.Control}  The instantiated view.
     * @protected
     */
    createView: function(ViewKind) {
        var view;

        // Create new view
        view = this.createComponent({
            kind:       ViewKind,
            owner:      this,
            container:  null,
            controller: this,
            state:      this.state
        });

        return view;
    },

    /**
     * Mounts the provided view in the viewContainer.
     *
     * @param  {enyo.Control}  view  The view.
     * @protected
     */
    mountView: function(view) {
        var viewContainer = this.viewContainer;

        if (!viewContainer) {
            throw new Error("View container required");
        }

        if (!(view instanceof Control)) {
            throw new Error("View must be an enyo.Control");
        }

        this.set("view", view);
        viewContainer.mountView(view);
    },

    /**
     * Unmounts the provided view from the viewContainer.
     *
     * @param  {enyo.Control}  view  The view.
     * @protected
     */
    unmountView: function(view) {
        var viewContainer = this.viewContainer;

        if (!viewContainer) {
            throw new Error("View container required");
        }

        if (!(view instanceof Control)) {
            throw new Error("View must be an enyo.Control");
        }

        viewContainer.unmountView(view);
        this.set("view", null);
    },

    /**
     * Unmounts the view and destroys it.
     *
     * @param  {enyo.Control}  view  The view.
     * @protected
     */
    destroyView: function(view) {
        var viewContainer = this.viewContainer;

        if (!(view instanceof Control)) {
            throw new Error("View must be an enyo.Control");
        }

        if (view && viewContainer) {
            this.unmountView(view);
        }
        view.destroy();
    },

    /**
     * If we have a viewContainer, we don't waterfall resize events to the view
     * ourselves. The container will do so already.
     *
     * @param     {String}         nom     The name of the event.
     * @param     {Object}         event   The event object to pass along.
     * @param     {enyo.Component} sender  The event originator.
     * @returns   {this}           The callee for chaining.
     * @memberof  app.core.ViewController
     * @public
     */
    waterfallDown: kind.inherit(function(sup) {
        return function(nom) {
            var view          = this.get("view"),
                viewContainer = view && view.get("container");

            if (viewContainer && (nom === "onresize" || nom === "onpostresize")) {
                return this;
            }

            return sup.apply(this, arguments);
        };
    }),

    /**
     * @protected
     */
    stateChanged: function() {
        this.view && this.view.set("state", this.state);
    }

});
